import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { RestAPIService } from 'app/services/rest-api.service';
import { CommonService } from 'app/shared/services/common.service';
import { IGetPerspective, IGroupPropertiesInstances } from 'app/services/graphqlApi.service';
import { PAsset, Perspective, PerspectiveTreeNode, PerspectivePayload, PerspectiveGroupPayload, IPerspectiveAggregation, IPerspectiveGroupDetail } from '../interfaces/interfaces';
import { getAggregateFunctions, getAssets, getCountConditionTypes, getGroupCoordinateProperties, getModelIdByAssetId, getPerspectives, getPerspectivesById } from 'app/shared/constants/gql';
import { parsePerspectiveApiResponse } from 'app/shared/data/perspective';
import { TreeNode, TreeNodeTypes } from 'app/shared/tree';
import { IDropdown } from './../../shared/interfaces/model';
export { getAssets } from 'app/shared/constants/gql';
import { IAggregationPayload } from 'app/assets/interfaces/interfaces';
import { TREE_NODE_TYPE } from '../constants';
import { RestRequestType } from 'app/shared/enums/rest-request-type.enum';

@Injectable({
  providedIn: 'root'
})

export class PerspectivesService {
  readonly selectedPerspective = new BehaviorSubject<Perspective | null>(null);
  readonly selectedPerspective$ = this.selectedPerspective.asObservable();

  readonly perspectives = new BehaviorSubject<Perspective[]>([]);
  readonly perspectives$ = this.perspectives.asObservable();

  readonly assetSummaryList = new BehaviorSubject<PAsset[]>([]);
  readonly assetSummaryList$ = this.assetSummaryList.asObservable();

  readonly _aggrFunctions = new BehaviorSubject<IDropdown[]>([]);
  readonly aggrFunctions$ = this._aggrFunctions.asObservable();

  readonly _savedAggregation = new BehaviorSubject<IPerspectiveAggregation>(null);
  readonly savedAggregation$ = this._savedAggregation.asObservable();

  readonly _selectedGroupInfo = new BehaviorSubject<IPerspectiveGroupDetail>(null);
  readonly selectedGroupInfo$ = this._selectedGroupInfo.asObservable();

  readonly _distableAddAggregateRoot = new BehaviorSubject<boolean>(false);
  readonly distableAddAggregateRoot$ = this._distableAddAggregateRoot.asObservable();

  // hide perspective table buttons
  readonly _assetTableButtons = new BehaviorSubject<boolean>(false);
  readonly assetTableButtons$ = this._assetTableButtons.asObservable();

  // for group table
  readonly _groupTableDetails = new BehaviorSubject<IPerspectiveAggregation[]>([]);
  readonly groupTableDetails$ = this._groupTableDetails.asObservable();

  constructor(
    private readonly apiService: RestAPIService,
    private readonly commonService: CommonService,
    private readonly toastr: ToastrService) { }

  getPerspectives() {
    return this.apiService.getGQLData({ query: getPerspectives.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetPerspective[] = response.data?.intellija_perspectives;
      return (queryResponse || []).map(parsePerspectiveApiResponse)
    }));
  }

  setPerspectives() {
    return this.getPerspectives().subscribe(perspectives => {
      this.perspectives.next(perspectives);
      this.mapUserGroupIntoPerspective(perspectives);
    });
  }

  private mapUserGroupIntoPerspective(perspectives: Perspective[]) {
    this.commonService.flatUserGroupList$.pipe(filter(groups => groups.length > 0))
      .subscribe(groups => {
        const userGroupMap = groups.reduce((groupMap, userGroup) => {
          groupMap.set(userGroup.id, userGroup);
          return groupMap;
        }, new Map());

        for (let p of perspectives) {
          p.userGroups = p.userGroups.reduce((ugList, ug) => {
            if (userGroupMap.has(ug.id)) {
              ugList.push(userGroupMap.get(ug.id));
            }

            return ugList;
          }, []);
        }
        this.perspectives.next(perspectives);
      });
  }

  getPerspectiveById(id: string) {
    return this.apiService.getGQLData({ query: getPerspectivesById.query, params: { id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetPerspective[] = response.data?.intellija_perspectives;
      return (queryResponse || []).map(parsePerspectiveApiResponse)
    }));
  }

  setPerspectiveById(id: string) {
    console.log('id: ', id);
    this.getPerspectiveById(id).pipe(take(1)).subscribe(perspective => {
      console.log('perspective: 2', perspective);
      this.selectedPerspective.next(perspective[0]);
      // this.getAggregations().pipe(take(1)).subscribe(aggregations =>{
      //   console.log('get aggregations: ', aggregations);
      // });
    });
  }

  getAssetSummaryList(): Observable<PAsset[]> {
    return this.apiService.getGQLData({ query: getAssets.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetPerspective[] = response.data?.intellija_entities;;
      return (queryResponse || []).map(record => {
        return {
          id: record.id,
          name: record.name,
          type: TREE_NODE_TYPE.leaf
        }
      });
    }));
  }

  setAssetSummaryList() {
    this.getAssetSummaryList().pipe(take(1)).subscribe(assets => {
      console.log('assets: ', assets);
      this.assetSummaryList.next(assets);
    });
  }

  movePerspectiveNode(pId: { sourceId: string, targetId: string }, nodeIndex: string = '') {
    let url = `perspective-trees/move-node/${pId.sourceId}/to/${pId.targetId}`;
    if (nodeIndex) {
      url += `?node_index=${nodeIndex}`;
    }
    return this.apiService.post<PerspectiveGroupPayload>(url, RestRequestType.MODE_PERSPECTIVE_NODE, null)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Perspective node moved successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  createPerspectiveNode(perspective: PerspectiveGroupPayload) {
    return this.apiService.post<PerspectiveGroupPayload>('perspective-trees', RestRequestType.CREATE_PERSPECTIVE_GROUP, perspective)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Perspective group created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  updatePerspectiveNode(perspective: PerspectiveGroupPayload) {
    return this.apiService.put<PerspectiveGroupPayload>('perspective-trees', RestRequestType.CREATE_PERSPECTIVE_GROUP, perspective)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Perspective node updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  deletePerspectiveNode(id: string) {
    return this.apiService.delete(`perspective-trees/${id}`)
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }),
        tap(res => {
          this.toastr.success('Perspective node deleted successfully!');
        }));
  }

  createPerspective(perspective: PerspectivePayload) {
    return this.apiService.post<PerspectivePayload>('perspectives', RestRequestType.CREATE_PERSPECTIVE, perspective)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Perspective created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  updatePerspective(perspective: PerspectivePayload) {
    return this.apiService.put<PerspectivePayload>('perspectives', RestRequestType.UPDATE_PERSPECTIVE, perspective)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Perspective updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  deletePerspective(id: string) {
    return this.apiService.delete(`perspectives/${id}`)
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }),
        tap(res => {
          this.toastr.success('Perspective deleted successfully!');
        }));
  }

  buildTree(perspective: Perspective) {
    let node: TreeNode | null = null;
    if (perspective?.perspectiveTree?.length > 0) {
      const rootNodeData = perspective.perspectiveTree.find(pt => !pt.parentId);
      if (rootNodeData) {
        node = this.buildParentNode(rootNodeData, TREE_NODE_TYPE.root);
        this.buildRecurringNode(node, perspective.perspectiveTree, rootNodeData);
      }
    }

    return node ? [node] : [];
  }

  private buildRecurringNode(node: TreeNode, treeNodes: PerspectiveTreeNode[], rootNodeData: PerspectiveTreeNode) {
    const childNodesData = treeNodes.filter(n => n.parentId === rootNodeData.id).sort((a, b) => b.nodeIndex - a.nodeIndex);
    if (childNodesData.length > 0) {
      for (let childData of childNodesData) {
        // if (childData.type == TREE_NODE_TYPE.group) {
        const assets = treeNodes.filter(tn => tn.type === TREE_NODE_TYPE.leaf && tn.parentId === childData.id);
        node.AddChild(this.buildNode(childData, assets));
        const newChildNode = node.getChildWithLabelName(childData.name);

        this.buildRecurringNode(newChildNode, treeNodes, childData);
        // }
      }
    } else {
      return;
    }
  }

  buildNode(nodeData: PerspectiveTreeNode, assets = []): TreeNode {
    return new TreeNode({
      label: nodeData.name,
      children: [],
      nodeType: TreeNodeTypes.NODE,
      expanded: true,
      data: {
        pGroupValue: {
          value: nodeData.name,
          id: nodeData.id,
          type: nodeData.type,
          parentId: nodeData.parentId,
          assets,
        },
      }
    });
  }

  private buildParentNode(nodeData: PerspectiveTreeNode, type: string = null) {
    return new TreeNode({
      label: nodeData?.name,
      children: [],
      nodeType: TreeNodeTypes.NODE,
      expanded: true,
      data: {
        pGroupValue: {
          value: nodeData?.name,
          id: nodeData?.id,
          parentId: nodeData?.parentId,
          type: type || nodeData?.type,
        },
      }
    });
  }

  buildAssetTree(assets: PerspectiveTreeNode[]) {
    return assets.map(a => this.buildParentNode(a));
  }

  /**
   * get aggregate functions
   */
  getAggregateFunctions() {
    return this.apiService.getGQLData({ query: getAggregateFunctions.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IDropdown[] = response.data?.intellija_ref_agg_functions;
      return (queryResponse || []).map(record => {
        return record || [];
      });
    }));
  }

  getModelIdByAssetId(id: string) {
    return this.apiService.getGQLData({ query: getModelIdByAssetId.query, params: { assetId: id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) =>
      (response.data?.intellija_entities || [])
    ));
  }

  /**
   * save perspective's aggregation
   * @param payload payload
   */
  saveAggregation(payload: IAggregationPayload) {
    return this.apiService.post('aggr-configs', RestRequestType.CREATE_AGGREGATE, payload).pipe(
      catchError(err => {
        this.toastr.error(err.error?.error?.message);
        return EMPTY;
      }));
  }

  /**
  * delete aggredate
  * @param assetId asset id
  * @param payload aggredate data
  */
  deleteAggregation(id: string) {
    return this.apiService.delete(`aggr-configs/${id}`)
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }));
  }

  /**
  * Put aggredate
  * @param payload aggregate data
  * @param aggregationId aggr id
  */
  updateAggregation(payload: IAggregationPayload, aggregationId: string) {
    return this.apiService.put(`aggr-configs/${aggregationId}`, RestRequestType.UPDATE_AGGREGATE, payload)
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }));
  }

  getCountConditionTypes() {
    return this.apiService.getGQLData({ query: getCountConditionTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: Array<{ name: string; display_name: string; is_deleted: boolean }> = response.data?.intellija_ref_count_condition_types;
      return (queryResponse || []).map(record => {
        return { name: record.display_name, value: record.name } as IDropdown || [];
      });
    }));
  }


  /**
   * get model coordinates properties that are overriden at asset level (for map panel)
   * @param groupId group id
   */
  getGroupCoordinateProperties(groupId: string) {
    return this.apiService.getGQLData({ query: getGroupCoordinateProperties.query, params: { perspectiveGroupId: groupId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGroupPropertiesInstances[] = response.data?.intellija_group_property_instances;
      return queryResponse.map(data => this.setGroupCoordinateProperties(data)) || [];
    }));
  }

  /**
 * set group coordinates properties // for map panel
 * @param prop resp prop
 */
  setGroupCoordinateProperties(prop: IGroupPropertiesInstances) {
    return {
      assetId: prop.asset_id,
      assetName: prop.asset_name,
      groupId: prop.group_id,
      modelId: prop.model_id,
      modelName: prop.model_name,
      componentId: prop.component_id,
      componentName: prop.component_name,
      propertyId: prop.property_id,
      propertyName: prop.property_name,
      propertyType: prop.property_type,
      componentInstanceId: prop.component_instance_id,
      componentInstanceName: prop.component_instance_name,
      isOverride: prop.is_override,
      value: prop.property_value
    }
  }

}